import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTRPC } from '../../lib/trpc'
import { Field, Modal, Notice, TextInput } from '../../ui'
import { useToast } from '../../ui/Toast'

/** The longest a folder's name may be, as the server holds it. */
const NAME_MAX = 120

/**
 * Name a new folder, or rename one. One fact, so a Modal: there is no record
 * here to keep beside a list, only a word to settle.
 */
export function FolderName({
  spaceId,
  folder,
  parentId = null,
  onDone,
  onClose,
}: {
  spaceId: string
  /** The folder being renamed; absent, a new one is made. */
  folder?: { id: string; name: string }
  /** Where a new folder goes; null is the top level. */
  parentId?: string | null
  onDone?: (id: string) => void
  onClose: () => void
}) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const toast = useToast()
  const [name, setName] = useState(folder?.name ?? '')
  const done = (id: string) => {
    qc.invalidateQueries(trpc.folders.list.queryFilter())
    /* A folder made or renamed inside one shared with the person is listed there. */
    qc.invalidateQueries(trpc.share.sharedFolders.queryFilter())
    onDone?.(id)
    onClose()
  }
  const create = useMutation(
    trpc.folders.create.mutationOptions({
      onSuccess: (made) => {
        toast(`Made ${made.name}.`)
        done(made.id)
      },
    }),
  )
  const rename = useMutation(
    trpc.folders.rename.mutationOptions({
      onSuccess: (renamed) => {
        toast(`Renamed to ${renamed.name}.`)
        done(renamed.id)
      },
    }),
  )
  const trimmed = name.trim()
  const pending = create.isPending || rename.isPending
  const error = create.error ?? rename.error
  const unchanged = folder ? trimmed === folder.name : false

  const commit = () => {
    if (!trimmed || unchanged || pending) return
    if (folder) rename.mutate({ spaceId, id: folder.id, name: trimmed })
    else create.mutate({ spaceId, name: trimmed, parentId })
  }

  return (
    <Modal
      title={folder ? `Rename ${folder.name}` : 'New folder'}
      onClose={onClose}
      primary={{
        label: folder ? 'Rename folder' : 'Make folder',
        disabled: !trimmed || unchanged || pending,
        onSelect: commit,
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          commit()
        }}
      >
        <Field label="Name">
          <TextInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={NAME_MAX}
            autoFocus
          />
        </Field>
      </form>
      {error ? <Notice tone="error">{error.message}</Notice> : null}
    </Modal>
  )
}
